// Fonte única da regra de eficiência do sistema (E.coli) para o dashboard SANEAMENTO.
// Usada pelo gráfico de tendência e pelo calendário/heatmap — para não divergirem.
//
// O limite é o LIMITE REGULATÓRIO (FEPAM / Resolução CONSEMA 355/2017) — a eficiência
// mínima de remoção que a estação precisa manter, definida pelo GESTOR a partir da
// Licença de Operação. É um piso legal (não uma "meta"). Ele chega em RUNTIME (por
// estação); os defaults abaixo só valem enquanto não configurado.
//
// 3 faixas (STATUS colors reservadas — sempre com rótulo/legenda + valor numérico):
//   < limite                       → Não conforme (crítico, vermelho)
//   [limite, limite + buffer)      → Atenção (âmbar, perto de furar o limite)
//   >= limite + buffer             → Conforme (verde)

export const LIMITE_PADRAO = 80; // % — usado até o gestor configurar a estação
export const BUFFER_PADRAO = 5;  // pontos percentuais da faixa de "atenção"

export const COR_META = '#2e9e5b';     // conforme (good)
export const COR_ATENCAO = '#e08a00';  // atenção (warning)
export const COR_CRITICO = '#d5433d';  // não conforme (critical)
export const COR_SEM = '#9aa0a6';      // sem análise

export function corEficiencia(valor, limite = LIMITE_PADRAO, buffer = BUFFER_PADRAO) {
    if (valor == null || Number.isNaN(valor)) return COR_SEM;
    if (valor < limite) return COR_CRITICO;
    if (valor < limite + buffer) return COR_ATENCAO;
    return COR_META;
}

/** Retorna { key, label, color } do estado de conformidade (para legenda/chips). */
export function statusEficiencia(valor, limite = LIMITE_PADRAO, buffer = BUFFER_PADRAO) {
    if (valor == null || Number.isNaN(valor)) {
        return { key: 'sem', label: 'Sem análise', color: COR_SEM };
    }
    if (valor < limite) {
        return { key: 'critico', label: `Não conforme (< ${limite}%)`, color: COR_CRITICO };
    }
    if (valor < limite + buffer) {
        return { key: 'atencao', label: 'Atenção — próximo do limite', color: COR_ATENCAO };
    }
    return { key: 'meta', label: `Conforme (≥ ${limite}%)`, color: COR_META };
}

/**
 * Percentual de eficiencia de remocao.
 *
 * NUNCA arredonda para cima cruzando 100. Em microbiologia 100% de remocao e uma
 * afirmacao que o dado nao sustenta: nao se prova ausencia por cultivo, so se
 * prova "abaixo do limite de deteccao". Arredondar 99,961 para "100,0%"
 * transforma "~1 sobrevivente a cada 2.600" em "nenhum sobrevivente" — e num
 * laudo que vai para a FEPAM isso nao e detalhe de formatacao. Foi exatamente
 * essa divergencia que apareceu em campo: o card dizia 100% e o laudo, 99,961%.
 *
 * Em vez de truncar a informacao, aumenta a precisao ate o numero parar de
 * mentir (99,961 -> "99,96%"; 99,9994 -> "99,999%"). Valores que nao encostam em
 * 100 seguem com as casas pedidas, entao o resto da tela nao muda de aparencia.
 *
 * Um valor >= 100 so acontece quando o efluente ficou abaixo do limite de
 * deteccao. Quem tem esse contexto (EficienciaResumo) mostra a frase em vez do
 * numero; aqui, sem contexto, o teto vira ">99,9%" para nao afirmar o absoluto.
 */
export const fmtPct = (v, casas = 1) => {
    if (v == null || Number.isNaN(v)) return '—';
    const n = Number(v);
    if (!Number.isFinite(n)) return '—';

    const br = (x, c) => `${x.toFixed(c).replace('.', ',')}%`;

    if (n >= 100) return '>99,9%';
    if (n < 0) return br(0, casas);

    for (let c = casas; c <= 3; c += 1) {
        if (Number(n.toFixed(c)) < 100) return br(n, c);
    }
    return '>99,999%';
};

/**
 * Log de remocao (LRV) a partir do percentual: -log10(1 - eff/100).
 *
 * E a metrica que a area usa porque o percentual satura justamente onde o
 * processo fica interessante: 99,9% / 99,99% / 99,999% parecem quase iguais e
 * estao 10x distantes uma da outra. O laudo ja imprime esse numero (calculado
 * direto das medias geometricas, AmostraReportService: log10(in/out)); aqui ele
 * e derivado do percentual, sem depender do backend — e os valores batem
 * (99,961% -> 3,4 log).
 *
 * Devolve null quando nao ha o que dizer (sem valor, 0%, ou efluente abaixo do
 * limite de deteccao — nesse ultimo caso o log seria infinito).
 */
export const fmtLogRemocao = (eficiencia) => {
    if (eficiencia == null || Number.isNaN(eficiencia)) return null;
    const n = Number(eficiencia);
    if (!Number.isFinite(n) || n <= 0 || n >= 100) return null;

    const log = -Math.log10(1 - n / 100);
    if (!Number.isFinite(log) || log <= 0) return null;

    return `${log.toFixed(1).replace('.', ',')} log`;
};

/**
 * Estado do bloco de resultado de UMA análise (card e modal usam o mesmo).
 * A ORDEM importa: um experimento rodando precede "sem resultado", e "calculando"
 * precede "inválida" (senão o usuário vê "inválida" durante o processamento).
 *
 * Campos esperados (vêm da listagem enriquecida em lote pelo backend):
 *   status, temResultado, resultadoStatus, eficienciaEcoli, eficienciaInvalida, eficienciaMotivo
 */
export function estadoResultado(a) {
    const st = String(a?.status || '').toUpperCase();
    const rs = String(a?.resultadoStatus || '').toUpperCase();

    // A ORDEM DESTES DOIS TESTES IMPORTA — e o comentario existe porque a inversao
    // nao e obvia:
    //
    // O worker de inferencia grava `EM_PROCESSAMENTO` NA AMOSTRA enquanto o modelo
    // roda (bioailab-inference-go, worker.go). So que esse mesmo valor significa
    // "ensaio rodando no dispositivo" em todo o resto do sistema — e quem o grava
    // nesse outro sentido e o MarkTowerProcessing do back-go. Ou seja: duas fases
    // completamente diferentes compartilham o mesmo valor no banco.
    //
    // O documento de resultado e o que desempata: ele so esta PENDENTE/PROCESSANDO
    // enquanto o modelo trabalha. Durante um ensaio de bancada ele nao existe, ou
    // esta CONCLUIDO/FALHO de um calculo anterior. Por isso o resultado e
    // consultado PRIMEIRO.
    if (st === 'ENVIADO' || rs === 'PENDENTE' || rs === 'PROCESSANDO') {
        return { key: 'calculando', label: 'Calculando resultado…', hint: null };
    }

    // "Em analise" = as amostras rodando NO DISPOSITIVO (horas). Nada vai ao modelo
    // aqui — o calculo so fica disponivel quando forem encerradas.
    if (st === 'EM_PROCESSAMENTO') {
        return {
            key: 'em-andamento',
            label: 'Análise em andamento',
            hint: 'As amostras estão sendo analisadas nos dispositivos. O cálculo fica disponível quando forem encerradas.',
        };
    }
    if (rs === 'FALHO') {
        return { key: 'falho', label: 'Falha no processamento', hint: a?.eficienciaMotivo || null };
    }
    if (!a?.temResultado) {
        return { key: 'sem-resultado', label: 'Sem resultado', hint: 'Use o botão Calcular.' };
    }
    if (a?.eficienciaInvalida === true) {
        return { key: 'invalida', label: 'Resultado inválido', hint: a?.eficienciaMotivo || null };
    }
    if (a?.eficienciaEcoli == null) {
        return {
            key: 'sem-eficiencia',
            label: 'Eficiência não calculável',
            hint: 'Faltam leituras de afluente e/ou efluente.',
        };
    }
    return { key: 'ok', label: null, hint: null };
}

/** Formata contagem (NMP) de forma compacta: 2.4M, 5.8k, 312. */
export function fmtContagem(v) {
    if (v == null || Number.isNaN(v)) return '—';
    const n = Number(v);
    if (n === 0) return '0';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
    return n.toFixed(n < 10 ? 1 : 0);
}
